
import React, { useState } from 'react';
import { Settings, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { setFeatureFlag, type FeatureFlags } from '@/config/features';

export const FeatureFlagPanel: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const flags = useFeatureFlags();

  // Only show on dashboard page - check both pathname and route
  const currentPath = window.location.pathname;
  const isDashboard = currentPath === '/' || currentPath === '/dashboard' || currentPath.endsWith('/');
  
  console.log('FeatureFlagPanel - current path:', currentPath, 'isDashboard:', isDashboard);
  
  if (!isDashboard) {
    return null;
  }

  const handleToggle = (flag: keyof FeatureFlags, value: boolean) => {
    setFeatureFlag(flag, value);
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        variant="outline"
        size="sm"
        className="fixed bottom-4 right-4 z-50 bg-background border-2"
      >
        <Settings className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <Card className="fixed bottom-4 right-4 z-50 w-80 max-h-96 overflow-y-auto">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Feature Flags</CardTitle>
          <Button
            onClick={() => setIsOpen(false)}
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <Label htmlFor="debug-mode" className="text-sm">Debug Mode</Label>
          <Switch
            id="debug-mode"
            checked={flags.debugMode}
            onCheckedChange={(checked) => handleToggle('debugMode', checked)}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label htmlFor="pro-features" className="text-sm">Pro Features</Label>
          <Switch
            id="pro-features"
            checked={flags.enableProFeatures}
            onCheckedChange={(checked) => handleToggle('enableProFeatures', checked)}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label htmlFor="collaboration" className="text-sm">Collaboration</Label>
          <Switch
            id="collaboration"
            checked={flags.enableCollaboration}
            onCheckedChange={(checked) => handleToggle('enableCollaboration', checked)}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label htmlFor="ai-analysis" className="text-sm">AI Analysis</Label>
          <Switch
            id="ai-analysis"
            checked={flags.enableAIAnalysis}
            onCheckedChange={(checked) => handleToggle('enableAIAnalysis', checked)}
          />
        </div>
        {/* Live Guidance flags are controlled by PostHog and not shown here */}
        <div className="flex items-center justify-between">
          <Label htmlFor="sharing" className="text-sm">Sharing</Label>
          <Switch
            id="sharing"
            checked={flags.enableSharing}
            onCheckedChange={(checked) => handleToggle('enableSharing', checked)}
          />
        </div>
      </CardContent>
    </Card>
  );
};
